import React, { useState, useEffect, useRef, useCallback } from 'react';
import ArticleCard from './ArticleCard';
import { getFilteredArticles, getSources } from '../services/api';

// ── Date preset helpers ────────────────────────────────────────────────────────

const DATE_PRESETS = [
  { label: 'All time', value: 'all' },
  { label: 'Today',    value: 'today' },
  { label: '7 days',   value: '7d' },
  { label: '30 days',  value: '30d' },
];

const SORT_OPTIONS = [
  { label: 'Latest first',   value: '-scraped_at' },
  { label: 'Most popular',   value: '-click_count' },
  { label: 'Top ranked',     value: 'rank' },
];

const PAGE_SIZE = 20;

function getFromDate(preset) {
  if (preset === 'all') return null;
  const d = new Date();
  if (preset === 'today') {
    d.setHours(0, 0, 0, 0);
  } else if (preset === '7d') {
    d.setDate(d.getDate() - 7);
  } else if (preset === '30d') {
    d.setDate(d.getDate() - 30);
  }
  return d.toISOString().split('T')[0]; // YYYY-MM-DD
}

// ── Icons ──────────────────────────────────────────────────────────────────────

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const ClearIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
    strokeLinecap="round" width="13" height="13">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const ChevronIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
    strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const FilterIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);

// ── Main component ─────────────────────────────────────────────────────────────

const NewsGrid = ({ initialArticles = [], onArticleClick, formatDate, truncateText, onCardClick }) => {

  // ── Filter state ─────────────────────────────────────────────────────────────
  const [search,      setSearch]      = useState('');
  const [source,      setSource]      = useState('');
  const [datePreset,  setDatePreset]  = useState('all');
  const [ordering,    setOrdering]    = useState('rank');

  // ── Data state ───────────────────────────────────────────────────────────────
  const [articles,    setArticles]    = useState(initialArticles);
  const [sources,     setSources]     = useState([]);
  const [totalCount,  setTotalCount]  = useState(initialArticles.length);
  const [hasMore,     setHasMore]     = useState(false);
  const [page,        setPage]        = useState(1);
  const [loading,     setLoading]     = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error,       setError]       = useState(null);

  // Whether any filters are active (used for the "clear filters" bar)
  const filtersActive = search || source || datePreset !== 'all' || ordering !== 'rank';

  // Debounce ref for search input
  const searchTimer = useRef(null);

  // ── Load sources for dropdown on mount ───────────────────────────────────────
  useEffect(() => {
    getSources()
      .then(data => setSources(data))
      .catch(err => console.error('Failed to load sources:', err));
  }, []);

  // ── Fetch articles whenever filters change ────────────────────────────────────
  const fetchArticles = useCallback(async (resetPage = true) => {
    const currentPage = resetPage ? 1 : page;
    if (resetPage) {
      setLoading(true);
      setPage(1);
    } else {
      setLoadingMore(true);
    }
    setError(null);

    try {
      const params = {
        ordering,
        page:      currentPage,
        page_size: PAGE_SIZE,
      };
      if (source)    params.source    = source;
      if (search)    params.search    = search;
      const fromDate = getFromDate(datePreset);
      if (fromDate)  params.from_date = fromDate;

      const data = await getFilteredArticles(params);

      if (resetPage) {
        setArticles(data.results || []);
      } else {
        setArticles(prev => [...prev, ...(data.results || [])]);
        setPage(currentPage + 1);
      }
      setTotalCount(data.count || 0);
      setHasMore(!!data.next);
    } catch (err) {
      setError('Failed to load articles. Please try again.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [source, search, datePreset, ordering, page]);

  // Re-fetch when filters change (not on page change — that's handled by loadMore)
  useEffect(() => {
    fetchArticles(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, datePreset, ordering]);

  // Debounced search — fires 400ms after user stops typing
  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      fetchArticles(true);
    }, 400);
    return () => clearTimeout(searchTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const loadMore = () => {
    if (!loadingMore && hasMore) {
      fetchArticles(false);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setSource('');
    setDatePreset('all');
    setOrdering('rank');
  };

  const activeSummary = [
    source && sources.find(s => s.slug === source)?.name,
    datePreset !== 'all' && DATE_PRESETS.find(d => d.value === datePreset)?.label,
    ordering !== 'rank' && SORT_OPTIONS.find(o => o.value === ordering)?.label,
  ].filter(Boolean).join(' · ');

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <main className="news-column" id="news-grid">

      {/* ── Filter bar ── */}
      <div style={s.filterBar}>

        {/* Search */}
        <div style={s.searchWrap}>
          <span style={s.searchIcon}><SearchIcon /></span>
          <input
            type="text"
            placeholder="Search articles…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={s.searchInput}
          />
          {search && (
            <button style={s.inputClear} onClick={() => setSearch('')} aria-label="Clear search">
              <ClearIcon />
            </button>
          )}
        </div>

        {/* Source dropdown */}
        <div style={s.selectWrap}>
          <select
            value={source}
            onChange={e => setSource(e.target.value)}
            style={s.select}
          >
            <option value="">All Sources</option>
            {sources.map(src => (
              <option key={src.id} value={src.slug}>{src.name}</option>
            ))}
          </select>
          <span style={s.selectChevron}><ChevronIcon /></span>
        </div>

        {/* Date preset pills */}
        <div style={s.pillGroup}>
          {DATE_PRESETS.map(p => (
            <button
              key={p.value}
              style={{
                ...s.pill,
                ...(datePreset === p.value ? s.pillActive : {}),
              }}
              onClick={() => setDatePreset(p.value)}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Sort dropdown */}
        <div style={s.selectWrap}>
          <select
            value={ordering}
            onChange={e => setOrdering(e.target.value)}
            style={s.select}
          >
            {SORT_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <span style={s.selectChevron}><ChevronIcon /></span>
        </div>

      </div>

      {/* ── Active filter summary strip ── */}
      {filtersActive && (
        <div style={s.activeBanner}>
          <span style={s.activeLeft}>
            <FilterIcon />
            <span style={{ marginLeft: 6 }}>
              {loading ? 'Filtering…' : `${totalCount} result${totalCount !== 1 ? 's' : ''}`}
              {activeSummary && <span style={s.activeTags}> · {activeSummary}</span>}
            </span>
          </span>
          <button style={s.clearBtn} onClick={clearFilters}>
            <ClearIcon /> Clear filters
          </button>
        </div>
      )}

      {/* ── Header ── */}
      {!filtersActive && (
        <h2 style={s.heading}>
          Latest Article Summaries
          <span style={s.count}>{totalCount}</span>
        </h2>
      )}

      {/* ── Error ── */}
      {error && (
        <div style={s.errorBox}>
          {error}
          <button style={s.retryBtn} onClick={() => fetchArticles(true)}>Retry</button>
        </div>
      )}

      {/* ── Loading skeleton ── */}
      {loading && (
        <div className="news-grid">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} style={s.skeleton}>
              <div style={s.skeletonImg} />
              <div style={{ padding: '1rem' }}>
                <div style={{ ...s.skeletonLine, width: '40%', marginBottom: 10 }} />
                <div style={{ ...s.skeletonLine, width: '90%', marginBottom: 8 }} />
                <div style={{ ...s.skeletonLine, width: '75%' }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Articles grid ── */}
      {!loading && (
        <>
          {articles.length === 0 ? (
            <div style={s.emptyState}>
              <p style={s.emptyTitle}>No articles found</p>
              <p style={s.emptySubtitle}>Try adjusting your filters or search terms</p>
              {filtersActive && (
                <button style={s.clearBtnLarge} onClick={clearFilters}>Clear all filters</button>
              )}
            </div>
          ) : (
            <div className="news-grid">
              {articles.map((article, i) => (
                <ArticleCard
                  key={`${article.id}-${i}`}
                  article={article}
                  onClick={onArticleClick}
                  formatDate={formatDate}
                  truncateText={truncateText}
                  onCardClick={onCardClick}
                />
              ))}
            </div>
          )}

          {/* ── Load more ── */}
          {hasMore && (
            <div style={s.loadMoreWrap}>
              <button
                style={{ ...s.loadMoreBtn, ...(loadingMore ? s.loadMoreBtnDisabled : {}) }}
                onClick={loadMore}
                disabled={loadingMore}
              >
                {loadingMore ? (
                  <>
                    <span style={s.spinner} />
                    Loading…
                  </>
                ) : (
                  `Load more articles`
                )}
              </button>
              <p style={s.loadMoreMeta}>
                Showing {articles.length} of {totalCount}
              </p>
            </div>
          )}
        </>
      )}

    </main>
  );
};

// ── Styles ─────────────────────────────────────────────────────────────────────

const s = {
  // Filter bar
  filterBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    flexWrap: 'wrap',
    padding: '1rem 1.25rem',
    background: '#f8fafc',
    borderRadius: '10px',
    border: '1px solid #e2e8f0',
    marginBottom: '0.75rem',
  },

  // Search
  searchWrap: {
    position: 'relative',
    flex: '1 1 200px',
    minWidth: 160,
  },
  searchIcon: {
    position: 'absolute',
    left: 10,
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#94a3b8',
    pointerEvents: 'none',
    display: 'flex',
  },
  searchInput: {
    width: '100%',
    padding: '0.55rem 2rem 0.55rem 2.1rem',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    fontSize: '0.875rem',
    background: '#fff',
    color: '#1a1a1a',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box',
  },
  inputClear: {
    position: 'absolute',
    right: 8,
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#94a3b8',
    display: 'flex',
    padding: 2,
  },

  // Select
  selectWrap: {
    position: 'relative',
    flexShrink: 0,
  },
  select: {
    appearance: 'none',
    padding: '0.55rem 2rem 0.55rem 0.85rem',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    fontSize: '0.875rem',
    background: '#fff',
    color: '#374151',
    cursor: 'pointer',
    outline: 'none',
    minWidth: 130,
  },
  selectChevron: {
    position: 'absolute',
    right: 8,
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
    color: '#94a3b8',
    display: 'flex',
  },

  // Date pills
  pillGroup: {
    display: 'flex',
    gap: '0.35rem',
    flexShrink: 0,
  },
  pill: {
    padding: '0.45rem 0.85rem',
    borderRadius: 20,
    border: '1px solid #e2e8f0',
    background: '#fff',
    color: '#64748b',
    fontSize: '0.8rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.15s',
    whiteSpace: 'nowrap',
  },
  pillActive: {
    background: '#1e40af',
    borderColor: '#1e40af',
    color: '#fff',
    fontWeight: 600,
  },

  // Active filter banner
  activeBanner: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.6rem 1rem',
    background: 'rgba(30,64,175,0.06)',
    border: '1px solid rgba(30,64,175,0.15)',
    borderRadius: 8,
    marginBottom: '1rem',
    fontSize: '0.85rem',
    color: '#374151',
  },
  activeLeft: {
    display: 'flex',
    alignItems: 'center',
    color: '#1e40af',
    fontWeight: 500,
  },
  activeTags: {
    color: '#64748b',
    fontWeight: 400,
  },
  clearBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    background: 'none',
    border: '1px solid #cbd5e1',
    borderRadius: 6,
    padding: '0.3rem 0.75rem',
    fontSize: '0.8rem',
    color: '#64748b',
    cursor: 'pointer',
    fontWeight: 500,
    transition: 'all 0.15s',
  },

  // Heading
  heading: {
    fontSize: '1.5rem',
    marginBottom: '1.5rem',
    color: '#1a1a1a',
    borderBottom: '2px solid #1e40af',
    paddingBottom: '0.75rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.6rem',
  },
  count: {
    fontSize: '0.9rem',
    fontWeight: 500,
    color: '#6b7280',
    background: '#f3f4f6',
    padding: '2px 10px',
    borderRadius: 12,
  },

  // Error
  errorBox: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: 8,
    padding: '1rem 1.25rem',
    color: '#dc2626',
    fontSize: '0.9rem',
    marginBottom: '1rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  retryBtn: {
    background: '#dc2626',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    padding: '0.4rem 1rem',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: 600,
  },

  // Skeleton
  skeleton: {
    background: '#fff',
    borderRadius: 10,
    border: '1px solid #e5e7eb',
    overflow: 'hidden',
  },
  skeletonImg: {
    height: 180,
    background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.4s infinite',
  },
  skeletonLine: {
    height: 14,
    borderRadius: 4,
    background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.4s infinite',
  },

  // Empty state
  emptyState: {
    textAlign: 'center',
    padding: '4rem 2rem',
    color: '#6b7280',
  },
  emptyTitle: {
    fontSize: '1.2rem',
    fontWeight: 700,
    color: '#374151',
    marginBottom: '0.5rem',
  },
  emptySubtitle: {
    fontSize: '0.95rem',
    marginBottom: '1.5rem',
  },
  clearBtnLarge: {
    background: '#1e40af',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '0.7rem 1.5rem',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
  },

  // Load more
  loadMoreWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.6rem',
    marginTop: '2.5rem',
    paddingTop: '1.5rem',
    borderTop: '1px solid #e5e7eb',
  },
  loadMoreBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    background: '#fff',
    color: '#1e40af',
    border: '2px solid #1e40af',
    borderRadius: 10,
    padding: '0.75rem 2.5rem',
    fontSize: '0.95rem',
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'all 0.2s',
    letterSpacing: '0.02em',
  },
  loadMoreBtnDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  loadMoreMeta: {
    fontSize: '0.8rem',
    color: '#9ca3af',
    margin: 0,
  },
  spinner: {
    display: 'inline-block',
    width: 14,
    height: 14,
    border: '2px solid #bfdbfe',
    borderTop: '2px solid #1e40af',
    borderRadius: '50%',
    animation: 'spin 0.7s linear infinite',
  },
};
export default NewsGrid;